import { getSpecifiedCountryFaqs } from './components/Faq/SpecificCountriesFaqUtils';
import {
  IAppContext,
  Feature,
  Denullified,
  OfferingProduct,
  ParsedDealerOffersMap,
  OfferingProductV2,
  ParsedPaymentMethods,
  ParsedAppIntentElements,
  AdvancedPlansFeature,
  AdvancedPlansProductData,
} from './types';
import { realDealFeatures as Features } from '@wix/premium-marketing-data/dist/src/marketing-data/real-deal/features';
import {
  Country,
  PaymentCycle,
  Theme,
  Template,
  RealEstateId,
  Experiments,
  SectionCategory,
  VoucherEligibleTlds,
} from './enums';
import { faqCodeMap, faqEnterpriseCodeMap } from './constants';
import { ExperimentsProviderOptions } from '@wix/wix-experiments-react';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { DateFormatter, FormatConfig } from '@wix/premium-react-text';
import { FeaturesBuilder } from './server/Builders/FeaturesBuilder';
import { StringMap } from 'i18next';
import dayjs from 'dayjs';

export const getFeatureTooltipKey = (
  feature: Feature,
  appContext: IAppContext,
) => {
  const { country } = appContext;
  if (
    feature.code === Features.free_domain_1_year.code &&
    (country === Country.FR || country === Country.BR || country === Country.DE)
  ) {
    return `features.${feature.code}_${country.toLowerCase()}.tooltip`;
  }
  return `features.${feature.code}.tooltip`;
};

export const isCycleOfMonths = (cycle: string) =>
  cycle === PaymentCycle.MONTHLY || cycle === PaymentCycle.QUARTERLY;

export const deNullify = (n: number | null): number => n ?? 0;

export function deNullifyProduct(product: OfferingProduct | OfferingProductV2) {
  return Object.entries(product).reduce(
    (acc, [key, value]) => ({ ...acc, ...{ [key]: deNullify(value) } }),
    {} as Denullified<OfferingProduct | OfferingProductV2>,
  );
}

export const throttle = (func: Function, timeFrame: number) => {
  let lastTime = 0;
  return function (this: any) {
    const now = Date.now();
    if (now - lastTime >= timeFrame) {
      func.apply(this, arguments);
      lastTime = now;
    }
  };
};

// export const throttle = (fn: Function, wait: number = 300) => {
//   let inThrottle: boolean,
//     lastFn: ReturnType<typeof setTimeout>,
//     lastTime: number;
//   return function (this: any) {
//     const context = this,
//       args = arguments;
//     if (!inThrottle) {
//       fn.apply(context, args);
//       lastTime = Date.now();
//       inThrottle = true;
//     } else {
//       clearTimeout(lastFn);
//       lastFn = setTimeout(() => {
//         if (Date.now() - lastTime >= wait) {
//           fn.apply(context, args);
//           lastTime = Date.now();
//         }
//       }, Math.max(wait - (Date.now() - lastTime), 0));
//     }
//   };
// };

export const getPageModeData = (isEditorXOffering: boolean) => {
  if (isEditorXOffering) {
    return {
      theme: Theme.EditorX,
      template: Template.Grid,
    };
  }
  return {
    theme: Theme.AdvancedPlans,
    template: Template.Grid,
  };
};

export function replaceTrigonalBrackets(str: string, replaceValue: string) {
  const pattern = /(<\d*\/>|(<\d*>)|(<\/\d*>))/g;
  return str.replace(pattern, replaceValue);
}

export const translateAllKeyInObject = (obj, t) => {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      return { ...acc, ...{ [key]: t(value) } };
    },
    {
      title: '',
      content: '',
      ctaText: '',
    },
  );
};

export const getAdVoucherTransParams = (
  currencyToString,
  amount,
  amountToSpend,
) => {
  const formatConfig: FormatConfig = { hideZeroDecimals: true };
  const voucherTransParams = { amountToSpend: '$25', amount: '$75' };
  try {
    voucherTransParams.amountToSpend = currencyToString(
      amountToSpend!,
      formatConfig,
    );
    voucherTransParams.amount = currencyToString(amount!, formatConfig);
  } catch {
    // ignore
  }
  return voucherTransParams;
};

export const getPaymentMethodsFromDealerOffers = (
  offers: ParsedDealerOffersMap,
) => {
  const acceptedPaymentMethodsOffer = offers.get(
    RealEstateId.AcceptPaymentMethods,
  ) as ParsedPaymentMethods;
  return acceptedPaymentMethodsOffer;
};

export function instanceOfOfferingProductV2(
  object: Denullified<OfferingProduct | OfferingProductV2>,
): object is Denullified<OfferingProductV2> {
  return (object as OfferingProductV2).cyclePrices !== undefined;
}

const FALLBACK_DISCOUNT_PERCENTAGE: number = 50;

export function getSaleDiscountPercentage(
  experiments: ExperimentsProviderOptions['experiments'],
  product: AdvancedPlansProductModel,
): number {
  let productDiscountPercentage = FALLBACK_DISCOUNT_PERCENTAGE;
  if (experiments[Experiments.PREM_1507_30_40_50] === 'true') {
    productDiscountPercentage =
      product.getSaleDiscountPercentage() || FALLBACK_DISCOUNT_PERCENTAGE;
  }

  return productDiscountPercentage;
}

export const getFaqs = ({
  hasGoogleVoucher,
  theme,
  isEnterprisePlanType = false,
  localizedFaqs,
  country,
}: {
  hasGoogleVoucher: boolean;
  theme: Theme;
  isEnterprisePlanType?: boolean;
  localizedFaqs?: boolean;
  country: Country;
}) => {
  // toDo Shabo - improve
  const themedFaqs = faqCodeMap[theme];
  if (localizedFaqs) {
    const faqs = getSpecifiedCountryFaqs({ country });
    if (faqs) {
      return faqs;
    }
  }
  return isEnterprisePlanType
    ? faqEnterpriseCodeMap
    : {
        planCode: hasGoogleVoucher
          ? themedFaqs.premiumPlan
          : themedFaqs.premiumPlanNoVoucher,
        canITryCode: themedFaqs.canITry,
        domainCode: themedFaqs.myDomain,
        customDomainCode: themedFaqs.customDomain,
        emailAddressCode: themedFaqs.emailAddress,
        voucherCode: hasGoogleVoucher
          ? themedFaqs.googleVoucher
          : themedFaqs.billingInfo,
        whatOnlinepaymentsCode: themedFaqs.whatOnlinePayments,
      };
};

export const getRecommendedSectionDataFromDealerOffers = (
  offers: ParsedDealerOffersMap,
  isSecondUpgrade: boolean,
) => {
  const { recommendedSection } = (isSecondUpgrade
    ? offers.get(RealEstateId.AppIntentElementsSecondUpgrade)
    : offers.get(
        RealEstateId.AppIntentElementsInitialPurchase,
      )) as ParsedAppIntentElements;
  const section =
    recommendedSection === SectionCategory.DEFAULT
      ? SectionCategory.COMPLETE_ECOMMERCE_PLATFORM
      : recommendedSection;
  const shouldShowBadge = recommendedSection !== SectionCategory.DEFAULT;

  return {
    recommendedSection: section,
    shouldShowBadgeByDealer: shouldShowBadge,
  };
};

export const getDiffBetweenDays = (start: Date, end: Date) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const startUtc = Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endUtc - startUtc) / MS_PER_DAY);
};

export const creditExplanationTooltipData = (
  currentPlanDetails,
  availableCredit,
  todayDate = new Date(),
) => {
  const plansExpireTime = currentPlanDetails?.planEndDate;
  const daysLeft = getDiffBetweenDays(
    todayDate,
    new Date(plansExpireTime || 0),
  );
  const credit = availableCredit;
  const pricePerDay = daysLeft >= 1 ? (credit / daysLeft).toFixed(2) : 0; // Not supposed to get here.  If the plan is complete, and there are no days left, there is no 2nd upgrade.
  return {
    plan: currentPlanDetails?.planName,
    pricePerDay: +pricePerDay,
    daysLeft: +daysLeft,
    credit: +availableCredit,
  };
};

export const getCycleKey = (paymentCycle: PaymentCycle | undefined) => {
  switch (paymentCycle) {
    case PaymentCycle.MONTHLY:
      return 'Advanced_plans.second_upgrade.cycle.monthly';
    case PaymentCycle.YEARLY:
      return 'Advanced_plans.second_upgrade.cycle.yearly';
    case PaymentCycle.YEAR2:
      return 'Advanced_plans.second_upgrade.cycle.2year';
    case PaymentCycle.YEAR3:
      return 'Advanced_plans.second_upgrade.cycle.3year';
    default:
      return '';
  }
};

const getDomainTld = (domainName: string | undefined) => {
  const dotIndex = domainName?.indexOf('.');
  return dotIndex ? domainName?.substring(dotIndex + 1) : '';
};

export const isEligibleForVoucher = (
  domainName: string | undefined,
): boolean => {
  const tld = getDomainTld(domainName);
  return (
    !!tld &&
    Object.values(VoucherEligibleTlds).includes(tld as VoucherEligibleTlds)
  );
};

export const wixResultInterceptor = (response) => {
  // Handling deprecated convention in premium-store response
  if (!response.data) {
    return response;
  }

  if (response.data.success === false) {
    response.status = 500;
    return Promise.reject(response);
  }

  if (response.data.success === true && response.data.payload !== undefined) {
    const payload = response.data.payload;
    if (typeof payload === 'object' && !Array.isArray(payload)) {
      delete response.data.payload;
      delete response.data.success;
      delete response.data.errorCode;
      delete response.data.errorDescription;
      response.data = { ...response.data, ...payload };
    } else {
      response.data = response.data.payload;
    }
  }

  return response;
};

export const getBuildFeaturesFunc = ({
  experiments,
  userCountry,
  domainName,
  productIds,
  language,
  isUpgrade,
  referralInfo,
  vouchersTypeEligibility,
  advancedPlansProducts,
  isChangePlan,
}: {
  experiments: StringMap;
  userCountry: Country;
  domainName?: string;
  referralInfo?: string;
  productIds: string[];
  language;
  isUpgrade: boolean;
  vouchersTypeEligibility?: string[] | null;
  advancedPlansProducts: AdvancedPlansProductData[];
  isChangePlan: boolean;
}) => {
  const buildFeatures = (advacnedFeatures: AdvancedPlansFeature[]) =>
    new FeaturesBuilder(advacnedFeatures)
      .filterOnlyOfferingRelated(productIds)
      .filterIncludedInProducts(productIds)
      .filterByExpriments(experiments)
      .applyGeoFiltering(userCountry)
      .applyLangFiltering(language)
      .useDynamicAdVoucherMarketingFeature(experiments)
      .changeOneYearDomainFeature({
        userCountry,
        experiments,
        domainName,
        referralInfo,
      })
      .applySecondUpgradeFilter({
        experiments,
        isUpgrade,
        vouchersTypeEligibility,
        advancedPlansProducts,
        isChangePlan,
      })
      .build();
  return buildFeatures;
};

export const getCountryCodeTld = (country: Country) => {
  const ccTldMap: Record<string, string> = {
    [Country.DE]: '.de',
    [Country.BR]: '.com.br',
    [Country.FR]: '.fr',
    [Country.BE]: '.fr', // we want to show .fr also in belgium
  };
  return ccTldMap[country];
};

const padNumber = (n: number, padLength: number, padWith: string = '0') => {
  const pad = new Array(1 + padLength).join(padWith);
  const paddedString = (pad + n).slice(-pad.length);
  return paddedString;
};

export const getSaleEndTimeString = (saleEndDate: number, country: Country) => {
  const US_HOUR_AND_TIMEZONE = '(11:59 PM PT)';

  if (country === Country.USA || country === Country.CA) {
    return US_HOUR_AND_TIMEZONE;
  } else {
    const date = new Date(saleEndDate);
    const hrsString = padNumber(date.getUTCHours(), 2);
    const minString = padNumber(date.getUTCMinutes(), 2);
    return `(${hrsString}:${minString} GMT)`;
  }
};

export const formatSaleEndDate = ({
  saleEndDate,
  country,
  formatter,
}: {
  saleEndDate: number;
  country: Country;
  formatter: DateFormatter;
}) => {
  const hourAndTimezone = getSaleEndTimeString(saleEndDate, country);
  if (country === Country.USA || country === Country.CA) {
    const date = dayjs(saleEndDate).subtract(1, 'day').valueOf();
    return formatter(date).concat(' ', hourAndTimezone);
  }
  return formatter(saleEndDate).concat(' ', hourAndTimezone);
};
